import { OffscreenCanvas } from "@/components/OffscreenCanvas";
import {
  DiceActionTypes,
  DiceRollActionTypes,
  getDiceServiceUrl,
  getMediaUrl,
  isDebug,
  WorkerPayloadTypes,
} from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { Context } from "@/context";
import { getManifestData } from "@/helpers/getManifestData";
import { playSound } from "@/helpers/playSound";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { CustomUrlMap, DiceSet, Manifest } from "@/types";
import { key } from "@dndbeyond/message-broker-lib/dist/lib/constants";
import { useGLTF, useTexture } from "@react-three/drei";
import { useContext, useEffect, useState, type FC } from "react";

interface DiceCanvasProps {
  worker?: Worker;
  gameId?: string;
  stt?: string;
  setId?: string;
  diceSets: DiceSet[];
  toastCallback?: (message: string) => void;
}

const preloadAssets = (
  mediaUrl: string,
  manifests: Manifest[],
  customUrlMap?: CustomUrlMap,
) => {
  Object.values(manifests).forEach((manifest) => {
    Object.entries(manifest.resources).forEach(([resourceType, resources]) => {
      Object.values(resources).forEach((resource) => {
        if (typeof resource === "object") return;

        if (resourceType === "models") {
          if (isDebug) {
            console.log(
              `[DiceCanvas] Preloading model: ${
                customUrlMap
                  ? customUrlMap[resource as string]
                  : `${mediaUrl}/dice/bundles/${manifest.slug}/${resource}`
              }`,
            );
          }

          useGLTF.preload(
            customUrlMap
              ? customUrlMap[resource as string]
              : `${mediaUrl}/dice/bundles/${manifest.slug}/${resource}`,
          );
        } else if (resourceType === "textures") {
          if (isDebug) {
            console.log(
              `[DiceCanvas] Preloading texture: ${
                customUrlMap
                  ? customUrlMap[resource as string]
                  : `${mediaUrl}/dice/bundles/${manifest.slug}/${resource}`
              }`,
            );
          }

          useTexture.preload(
            customUrlMap
              ? customUrlMap[resource as string]
              : `${mediaUrl}/dice/bundles/${manifest.slug}/${resource}`,
          );
        } else {
          (async () => {
            if (isDebug) {
              console.log(
                `[DiceCanvas] Preloading resource: ${
                  customUrlMap
                    ? customUrlMap[resource as string]
                    : `${mediaUrl}/dice/bundles/${manifest.slug}/${resource}`
                }`,
              );
            }

            await fetch(
              customUrlMap
                ? customUrlMap[resource as string]
                : `${mediaUrl}/dice/bundles/${manifest.slug}/${resource}`,
            );
          })();
        }
      });
    });
  });

  if (isDebug) {
    console.log(
      `[DiceCanvas] Preloading common noise map texture: ${mediaUrl}/dice/bundles/basic/images/noise-map.png`,
    );
  }

  useTexture.preload(`${mediaUrl}/dice/bundles/basic/images/noise-map.png`);

  if (isDebug) {
    console.log(
      `[DiceCanvas] Preloading common environment texture: ${mediaUrl}/dice/images/common/environment.jpg`,
    );
  }

  useTexture.preload(`${mediaUrl}/dice/images/common/environment.jpg`);
};

export const DiceCanvas: FC<DiceCanvasProps> = ({
  worker,
  gameId,
  stt,
  setId,
  diceSets,
  toastCallback,
}) => {
  const [manifests, setManifests] = useState<Record<string, Manifest>>();
  const [isPreloadComplete, setIsPreloadComplete] = useState(false);
  const { nodeEnv, env, user } = useContext(Context);
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const diceServiceUrl = getDiceServiceUrl(String(nodeEnv), String(env));
  const { getUserDiceData } = useUserDiceDataStore();
  const settings = getUserDiceData(user.id)?.settings;
  const volume = settings?.volume || 1;
  const shadowQuality = settings?.shadowQuality || 3;

  useEffect(() => {
    const handleMessage = async ({ data: { type, payload } }: MessageEvent) => {
      if (isDebug) {
        console.log("[DiceCanvas] Message from worker:", type, payload);
      }

      if (type === DiceRollActionTypes.Fulfilled) {
        window[key]?.dispatch(payload);
      } else if (type === WorkerPayloadTypes.PlaySound) {
        await playSound(payload.url, payload.collisionVolume * (volume ?? 1));
      } else if (type === WorkerPayloadTypes.ComponentMounted) {
        const data: Record<string, Manifest> = {};
        const setIds: string[] = [];

        if (gameId && stt) {
          try {
            const { data } = await (
              await fetch(
                `${diceServiceUrl}/diceuserconfig/v1/getsetidsincampaign/${gameId}`,
                {
                  headers: {
                    Authorization: `Bearer ${stt}`,
                  },
                },
              )
            ).json();

            setIds.push(...data);
          } catch (error) {
            if (isDebug) {
              console.error(
                `[DiceCanvas] Error fetching set IDs for game ID "${gameId}":`,
                error,
              );
            }
          }
        } else {
          if (setId) {
            setIds.push(setId);
          }
        }

        await Promise.all(
          setIds.map(async (setId) => {
            const manifestUrl =
              customUrlMap?.[customSetManifestFileName] ||
              diceSets.find(({ id }) => id === setId)?.manifestUrl;

            if (!manifestUrl) {
              if (isDebug) {
                console.warn(
                  `[DiceCanvas] Manifest URL for set ID "${setId}" not found.`,
                );
              }

              return;
            }

            data[setId] = await getManifestData({
              mediaUrl,
              setId,
              manifestUrl,
              customUrlMap,
            });
          }),
        );

        setManifests(data);
        worker?.postMessage({
          type: WorkerPayloadTypes.Manifests,
          payload: data,
        });

        if (isDebug) {
          console.log("[DiceCanvas] Loaded manifests:", data);
        }

        worker?.postMessage({
          type: WorkerPayloadTypes.ShadowQuality,
          payload: shadowQuality,
        });

        if (isDebug) {
          console.log(
            "[DiceCanvas] Sent shadow quality to worker:",
            shadowQuality,
          );
        }

        worker?.postMessage({
          type: WorkerPayloadTypes.DiceSets,
          payload: diceSets,
        });

        if (isDebug) {
          console.log("[DiceCanvas] Sent dice sets to worker:", diceSets);
        }
      } else if (type === DiceActionTypes.SetUpdate) {
        preloadAssets(mediaUrl, [payload], customUrlMap);
      } else if (type === WorkerPayloadTypes.ToastMessage) {
        toastCallback?.(payload);
      }
    };

    worker?.addEventListener("message", handleMessage);

    return () => {
      worker?.removeEventListener("message", handleMessage);
    };
  }, [
    customUrlMap,
    diceServiceUrl,
    diceSets,
    gameId,
    mediaUrl,
    setId,
    shadowQuality,
    stt,
    toastCallback,
    volume,
    worker,
  ]);

  useEffect(() => {
    if (!manifests || isPreloadComplete) return;

    preloadAssets(mediaUrl, Object.values(manifests), customUrlMap);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsPreloadComplete(true);
  }, [customUrlMap, isPreloadComplete, manifests, mediaUrl]);

  return (
    worker && (
      <OffscreenCanvas
        worker={worker}
        frameloop="demand"
        shadows
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999999999,
          pointerEvents: "none",
        }}
      />
    )
  );
};
